import React, { useState } from 'react'
import { Modal, Icon } from 'antd'
import moment from 'moment'

// Preview of what "Auto-resolve duplicates" will set, before anything changes.
// The plan comes from planDuplicates (duplicateAuto.js). Applying only stages the
// decisions in the cockpit: nothing reaches the server until Save, and nothing
// touches the register until Process.

const ACTION_LABEL = { IGNORE: 'Ignore', ATA_ARN: 'Transfer', NEW_ASSET: 'New asset' }
const ACTION_CLS = { IGNORE: 'ign', ATA_ARN: 'trf', NEW_ASSET: 'new' }

const DuplicateAutoModal = ({ plan, onApply, onCancel, place, regPlace }) => {
  const [showSkipped, setShowSkipped] = useState(false)
  if (!plan) return null
  const { groups, skipped } = plan
  const all = groups.reduce((acc, g) => acc.concat(g.rows), [])
  const count = (a) => all.filter(x => x.action === a).length
  const short = all.filter(x => x.action === 'NEW_ASSET' && (!x.details.ownedBy || x.details.rate === undefined)).length

  return (
    <Modal visible width={1080} className="rcx-dupauto" destroyOnClose maskClosable={false}
      title="Auto-resolve duplicate scans"
      okText={`Apply to ${all.length} scan${all.length === 1 ? '' : 's'}`}
      okButtonProps={{ disabled: !all.length }}
      onOk={onApply} onCancel={onCancel}>
      <div className="rcx">
        <div className="dupa-rule">
          The scan closest to the register location / sub-location <b>as on its scan date</b> keeps the
          code: <b>Ignore</b> if both match, otherwise <b>Transfer</b> to where it was scanned. Equally close
          scans: the earliest wins. Every other scan becomes a <b>New asset</b> (owner and invoice value copied
          from the coded asset) — unless it was scanned at the same place as one already resolved, which is the
          same item scanned again and is <b>Ignored</b>.
        </div>

        <div className="dupa-sum" role="status">
          <span><b className="tnum">{groups.length}</b> code{groups.length === 1 ? '' : 's'} resolved</span>
          <span className="dupa-a ign"><b className="tnum">{count('IGNORE')}</b> Ignore</span>
          <span className="dupa-a trf"><b className="tnum">{count('ATA_ARN')}</b> Transfer</span>
          <span className="dupa-a new"><b className="tnum">{count('NEW_ASSET')}</b> New asset</span>
          {skipped.length > 0 && (
            <span className="dupa-skip"><b className="tnum">{skipped.length}</b> code{skipped.length === 1 ? '' : 's'} left for manual review</span>
          )}
        </div>
        {short > 0 && (
          <div className="dupa-warn" role="alert">
            <Icon type="exclamation-circle" theme="filled" /> {short} new asset{short === 1 ? '' : 's'} could not
            copy an owner or invoice value because the coded asset has none. Process will stop on {short === 1 ? 'it' : 'them'} until
            the value is entered (click the row's details).
          </div>
        )}

        {groups.length > 0 ? (
          <div className="dupa-wrap">
            <table className="dupa-t">
              <thead>
                <tr>
                  <th>Code</th><th>Scanned at</th><th>Scan date</th><th>Register on date</th><th>Action</th><th>Why</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(g => g.rows.map((x, i) => (
                  <tr key={x.r._uid} className={i === 0 ? 'first' : ''}>
                    <td className="mono">{i === 0 ? g.code : ''}</td>
                    <td>{place(x.r) || '—'}</td>
                    <td className="tnum">{x.r.time ? moment(x.r.time).format('DD MMM YYYY HH:mm') : '—'}<div className="dupa-sub">{x.r.astNo || ''}</div></td>
                    <td>{regPlace(x.r) || '—'}</td>
                    <td><span className={'dupa-a ' + ACTION_CLS[x.action]}>{ACTION_LABEL[x.action]}</span></td>
                    <td className="dupa-why">{x.why}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="dupa-empty">No duplicate group can be resolved automatically.</div>
        )}

        {skipped.length > 0 && (
          <div className="dupa-skipped">
            <button type="button" className="dupa-toggle" aria-expanded={showSkipped}
              onClick={() => setShowSkipped(!showSkipped)}>
              <Icon type={showSkipped ? 'down' : 'right'} /> {skipped.length} code{skipped.length === 1 ? '' : 's'} not resolved automatically — why
            </button>
            {showSkipped && (
              <ul>
                {skipped.map(s => (
                  <li key={s.code}><span className="mono">{s.code}</span> · {s.rows.length} scan{s.rows.length === 1 ? '' : 's'} · {s.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="dupa-foot">
          Applying only sets the decisions here. Review them in the AST tab, then <b>Save</b> and <b>Process</b> as usual.
        </div>
      </div>
    </Modal>
  )
}

export default DuplicateAutoModal
